/* eslint-disable indent */
import './style.scss';

import { useEffect, useMemo, useState } from 'react';

import { Button } from 'primereact/button';
import { classNames } from 'primereact/utils';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { useParams } from 'react-router-dom';

import {
  downloadExplanation,
  downloadFile,
  downloadScientificProfileFile,
  downloadZip,
  getAllProposedExpenditureTypes,
  getAllVersions,
  getDocumentsById,
  getExpenditureById,
  getGeneralInfoById,
  getResearchResultById,
  getReviseLogs,
} from 'apis/projectProposals.api';

import {
  Documents,
  GeneralInfo,
  ReasearchDescripton,
  ResearchResult,
} from 'features/ReviseProjectProposal/components/Detail';

import ErrorDialog from 'components/ErrorDialog';
import Loading from 'components/Loading';

import {
  defaultExpenditureValues,
  defaultGeneralInfoValues,
} from 'features/ReviseProjectProposal/defaultValues';

import { Can } from 'components/Can';

import { PROJECT_ROLES, TOAST } from 'constant';
import ExpenditureSection from 'features/ReviseProjectProposal/components/Detail/GeneralInfo/ExpenditureSection';
import { toast } from 'layout';
import {
  formatFileName,
  formatTimeMarkerInPdf,
  getAcadamicLevelShortHand,
  getYearFromTemplate,
  is1593,
  modifyProjectProposalResearchResultReviewData,
  toNonAccentVietnamese,
} from 'utils/func';
import { useGetResearchKpis } from 'utils/hooks/projectProposals';

export default function ReviseProjectProposalDetail() {
  // #region Data
  const { t } = useTranslation();
  const { projectProposalId } = useParams();
  const [isErrorDialogOpen, setIsErrorDialogOpen] = useState(false);
  const [layoutYear, setLayoutYear] = useState(2019);

  const { mutate: mutateDownloadFile } = useMutation(downloadFile);
  const { mutate: mutateDownloadScientificProfileFile } = useMutation(
    downloadScientificProfileFile
  );

  const {
    mutate: mutateDownloadZip,
    isLoading: isDownloadZipLoading,
    isError: isDownloadZipError,
    error: downloadZipError,
  } = useMutation(downloadZip);

  const {
    mutate: mutateDownloadExplanation,
    isLoading: isDownloadExplanationLoading,
    isError: isDownloadExplanationError,
    error: downloadExplanationError,
  } = useMutation(downloadExplanation);

  const {
    data: reviseLogData,
    isLoading: isReviseLogLoading,
    isError: isReviseLogError,
    error: reviseLogError,
  } = useQuery(['projectProposalReviseLogs', projectProposalId, 'ALL_REVISE_LOG'], () =>
    getReviseLogs({
      projectProposalId,
      types: [
        'REQUEST_EXPLANATION_PREQUALIFICATION',
        'REQUEST_EXPLANATION_SPECIALIZATION',
        'REQUEST_EXPLANATION_FINANCE',
      ],
    })
  );

  const { data: allProjectProposalVersionsData } = useQuery(
    ['allProjectProposalVersions', projectProposalId],
    () => getAllVersions(projectProposalId)
  );

  const latestVersion = useMemo(() => {
    if (allProjectProposalVersionsData?.data?.length > 0) {
      return (
        allProjectProposalVersionsData?.data?.reduce((a, b) => (a?.version > b?.version ? a : b)) ??
        null
      );
    }
    return null;
  }, [allProjectProposalVersionsData]);

  const {
    data: generalInfoData,
    isLoading: isGeneralInfoLoading,
    isError: isGeneralInfoError,
    error: generalInfoError,
  } = useQuery(
    ['projectProposal-generalInfo', latestVersion?.id],
    () => getGeneralInfoById(latestVersion?.id),
    {
      enabled: !!latestVersion?.id,
    }
  );
  const generalInfo = useMemo(
    () => generalInfoData?.data ?? defaultGeneralInfoValues,
    [generalInfoData]
  );

  const {
    data: researchResultData_,
    isLoading: isResearchResultLoading,
    isError: isResearchResultError,
    error: researchResultError,
  } = useQuery(
    ['projectProposal-researchResult', latestVersion?.id],
    () => getResearchResultById(latestVersion?.id),
    {
      enabled: !!latestVersion?.id,
    }
  );

  const { data: researchKpis } = useGetResearchKpis(
    getYearFromTemplate(latestVersion?.program?.template, latestVersion?.yearProposed),
    {
      enabled: !!latestVersion?.id,
    }
  );

  const researchResult = useMemo(() => {
    const researchResultData = {
      ...researchResultData_?.data,
      projectProposalResearchKpis: researchResultData_?.data?.projectProposalResearchKpis?.reduce(
        (acc, curr) => {
          acc[curr.researchKpi?.id] = { quantity: curr.quantity, content: curr.content };
          return acc;
        },
        {}
      ),
    };
    return modifyProjectProposalResearchResultReviewData(researchResultData, researchKpis) ?? {};
  }, [researchResultData_, researchKpis]);

  const { data: expenditureTypesData } = useQuery(
    ['projectProposal-expenditure-types'],
    getAllProposedExpenditureTypes
  );

  const {
    data: expenditureData,
    isLoading: isExpenditureLoading,
    isError: isExpenditureError,
    error: expenditureError,
  } = useQuery(
    ['projectProposal-expenditure', latestVersion?.id],
    () => getExpenditureById(latestVersion?.id),
    { enabled: !!latestVersion?.id }
  );

  const expenditure = useMemo(() => {
    if (expenditureData?.data && expenditureTypesData?.data) {
      return {
        ...expenditureData.data,
        proposedExpenditureSummaries:
          expenditureData.data.proposedExpenditureSummaries.length === expenditureData?.data?.length
            ? expenditureData.data.proposedExpenditureSummaries
            : expenditureTypesData.data.map((_expenditure, index) => {
                const expenditureSummaries = expenditureData.data.proposedExpenditureSummaries.find(
                  (e) => e.proposedExpenditureTypeId === _expenditure.id
                );
                let id = index;
                let expenditureFromVNUHCM = 0;
                let expenditureFromFunding = 0;
                if (expenditureSummaries) {
                  id = expenditureSummaries.id;
                  expenditureFromVNUHCM = expenditureSummaries.expenditureFromVNUHCM;
                  expenditureFromFunding = expenditureSummaries.expenditureFromFunding;
                }
                return {
                  id,
                  expenditureFromVNUHCM,
                  expenditureFromFunding,
                  proposedExpenditureTypeId: _expenditure.id,
                  proposedExpenditureType: _expenditure,
                };
              }),
      };
    }
    return defaultExpenditureValues;
  });

  const expenditureInGeneral = useMemo(() => {
    const totalExpenditure =
      expenditure?.proposedExpenditureSummaries?.reduce((sum, cur) => {
        if (cur?.proposedExpenditureType?.subProposedExpenditureTypeId === null) {
          const expenditureFromFunding = cur?.expenditureFromFunding ?? 0;
          const expenditureFromVNUHCM = cur?.expenditureFromVNUHCM ?? 0;

          return sum + expenditureFromFunding + expenditureFromVNUHCM;
        }
        return sum;
      }, 0) ?? 0;
    const nationalUniversityTotalExpenditure =
      expenditure?.proposedExpenditureSummaries?.reduce((sum, cur) => {
        if (cur?.proposedExpenditureType?.subProposedExpenditureTypeId === null) {
          const expenditureFromVNUHCM = cur?.expenditureFromVNUHCM ?? 0;

          return sum + expenditureFromVNUHCM;
        }
        return sum;
      }, 0) ?? 0;
    let nationalUniversityNonallocatedExpenditure = 0;
    if (expenditure?.proposedExpenditureSummaries?.length === 4) {
      nationalUniversityNonallocatedExpenditure =
        expenditure?.proposedExpenditureSummaries?.[1]?.expenditureFromVNUHCM;
    } else if (expenditure?.proposedExpenditureSummaries?.length === 7) {
      nationalUniversityNonallocatedExpenditure =
        expenditure?.proposedExpenditureSummaries?.[4]?.expenditureFromVNUHCM;
    } else if (expenditure?.proposedExpenditureSummaries?.length === 8) {
      nationalUniversityNonallocatedExpenditure =
        expenditure?.proposedExpenditureSummaries?.[5]?.expenditureFromVNUHCM;
    }
    const nationalUniversityAllocatedExpenditure =
      nationalUniversityTotalExpenditure - nationalUniversityNonallocatedExpenditure;

    const fundingExpenditure = totalExpenditure - nationalUniversityTotalExpenditure;
    const fundingExpenditureOther = generalInfoData?.data?.fundingExpenditureOther ?? 0;
    const fundingExpenditureSelf = fundingExpenditure - fundingExpenditureOther;
    return {
      totalExpenditure,
      nationalUniversityTotalExpenditure,
      nationalUniversityNonallocatedExpenditure,
      nationalUniversityAllocatedExpenditure,
      fundingExpenditure,
      fundingExpenditureOther,
      fundingExpenditureSelf,
    };
  });

  const {
    data: documentsData,
    isLoading: isDocumentsLoading,
    isError: isDocumentsError,
    error: documentsError,
  } = useQuery(
    ['projectProposal-documents', latestVersion?.id],
    () => getDocumentsById(latestVersion?.id),
    { enabled: !!latestVersion?.id }
  );
  const documents = useMemo(() => documentsData?.data ?? {}, [documentsData]);

  const symbolString = useMemo(() => {
    const lastUpdatedTime = formatTimeMarkerInPdf(
      latestVersion?.updatedAt ?? latestVersion?.createdAt
    );

    const latestVersionId = latestVersion?.id?.slice(-12);

    return latestVersionId ? `${latestVersion?.id?.slice(-12) ?? ''}${lastUpdatedTime}` : '';
  }, [latestVersion]);
  // #endregion Data

  // #region Event
  useEffect(() => {
    localStorage.setItem('template', generalInfo?.template || null);
    localStorage.setItem('yearProposed', generalInfo?.yearProposed || layoutYear);
    if (generalInfo?.yearProposed > 0) {
      setLayoutYear(generalInfo?.yearProposed);
    }
  }, [generalInfo]);

  const handleDownloadScientificProfileFile = (
    contactId,
    contactName = '',
    academicRank = '',
    academicDegree = ''
  ) => {
    if (contactId && contactName) {
      mutateDownloadScientificProfileFile(contactId, {
        onSuccess: async (res) => {
          if (res?.data) {
            const file = new Blob([res.data]);
            const link = document.createElement('a');
            link.href = URL.createObjectURL(file);
            link.download = `${getAcadamicLevelShortHand(
              academicRank,
              academicDegree,
              t,
              true
            )}${toNonAccentVietnamese(contactName)}.pdf`;

            link.click();
          }
        },
        onError: () => {
          toast(
            TOAST.ERROR,
            t('projectProposal.general.researchHumanResources.downloadScientificProfileErrorTitle')
          );
        },
      });
    }
  };

  const handleDownloadFile = (path) => {
    mutateDownloadFile(
      { projectProposalId: latestVersion?.id, path },
      {
        onSuccess: async (res) => {
          if (res?.data) {
            const file = new Blob([res.data]);

            const link = document.createElement('a');
            link.href = URL.createObjectURL(file);
            link.download = formatFileName(path);

            link.click();
          }
        },
      }
    );
  };

  const handleDownloadZip = () => {
    // Call api download files
    // param:selectedItems,unselectedItems,isSelectedAll,isUnselectedSome,researchFieldId,statusName
    mutateDownloadZip(
      {
        selectedItems: [latestVersion?.id],
        unselectedItems: [],
        isSelectedAll: false,
        isUnselectedSome: false,
      },
      {
        onSuccess: (res) => {
          if (res?.data) {
            const file = new Blob([res.data]);

            const owner = generalInfo?.projectProposalContacts.find(
              (e) => PROJECT_ROLES.OWNER === e.projectRole.code
            );
            const academicLevelShorthand = getAcadamicLevelShortHand(
              owner?.academicRank,
              owner?.academicDegree,
              t,
              true
            );
            const contentDisposition = res?.headers['content-disposition'];
            const filenameMatch = contentDisposition?.match(/filename='(.+)'/);
            const filename = filenameMatch
              ? filenameMatch[1]
              : `De tai DHQG - ${academicLevelShorthand}${toNonAccentVietnamese(
                  owner?.fullname
                )}.zip`;
            const link = document.createElement('a');
            link.href = URL.createObjectURL(file);
            link.download = filename;

            link.click();
          }
        },
        onError: () => {
          setIsErrorDialogOpen(true);
        },
      }
    );
  };

  const handleDownloadExplanation = () => {
    mutateDownloadExplanation(projectProposalId, {
      onSuccess: (res) => {
        if (res?.data) {
          const file = new Blob([res.data]);

          const owner = generalInfo?.projectProposalContacts.find(
            (e) => PROJECT_ROLES.OWNER === e.projectRole.code
          );
          const academicLevelShorthand = getAcadamicLevelShortHand(
            owner?.academicRank,
            owner?.academicDegree,
            t,
            true
          );
          const contentDisposition = res?.headers['content-disposition'];
          const filenameMatch = contentDisposition?.match(/filename='(.+)'/);
          const filename = filenameMatch
            ? filenameMatch[1]
            : `Danh sach cac giai trinh de tai ĐHQG - ${academicLevelShorthand}${toNonAccentVietnamese(
                owner?.fullname
              )}.zip`;

          const link = document.createElement('a');
          link.href = URL.createObjectURL(file);
          link.download = filename;

          link.click();
        }
      },
      onError: () => {
        setIsErrorDialogOpen(true);
      },
    });
  };
  // #endregion Event

  // #region Render
  if (isReviseLogError) {
    return (
      <ErrorDialog
        title={t('projectProposal.reviseLogErrorTitle')}
        isError={isReviseLogError}
        error={reviseLogError}
        visible
        isNavigate
      />
    );
  }
  if (isGeneralInfoError) {
    return (
      <ErrorDialog
        title={t('projectProposal.generalInfoErrorTitle')}
        isError={isGeneralInfoError}
        error={generalInfoError}
        visible
        isNavigate
      />
    );
  }
  if (isResearchResultError) {
    return (
      <ErrorDialog
        title={t('projectProposal.researchResultErrorTitle')}
        isError={isResearchResultError}
        error={researchResultError}
        visible
        isNavigate
      />
    );
  }
  if (isExpenditureError) {
    return (
      <ErrorDialog
        title={t('projectProposal.expenditureErrorTitle')}
        isError={isExpenditureError}
        error={expenditureError}
        visible
        isNavigate
      />
    );
  }
  if (isDocumentsError) {
    return (
      <ErrorDialog
        title={t('projectProposal.documentsErrorTitle')}
        isError={isDocumentsError}
        error={documentsError}
        visible
        isNavigate
      />
    );
  }

  return (
    <div className="bg-white p-5">
      <div className="flex justify-content-end">
        <Can do="read" on="project_proposal">
          <Button
            type="button"
            label={t('projectProposal.viewList.tooltip.download')}
            icon={classNames(
              'pi',
              { 'pi-spin pi-spinner': isDownloadZipLoading },
              { 'pi pi-download': !isDownloadZipLoading }
            )}
            severity="info"
            onClick={handleDownloadZip}
          />
        </Can>
        <Can do="read" on="project_proposal">
          {reviseLogData?.data?.length > 0 && (
            <Button
              type="button"
              label="Tải giải trình đề tài"
              icon={classNames(
                'pi',
                { 'pi-spin pi-spinner': isDownloadExplanationLoading },
                { 'pi pi-file': !isDownloadExplanationLoading }
              )}
              className="ml-1"
              severity="info"
              onClick={handleDownloadExplanation}
            />
          )}
        </Can>
      </div>

      {(isReviseLogLoading ||
        isGeneralInfoLoading ||
        isResearchResultLoading ||
        isExpenditureLoading ||
        isDocumentsLoading) && <Loading />}
      <div className="m-0 bg-white p-5 p-fluid detail-info-project-proposal">
        <h3 className="line-height-2 text-4xl text-center font-bold">
          {t('projectProposal.viewDetail.presentation')}
          <br />
          <span className="font-normal">{t('projectProposal.viewDetail.sciTechProject')}</span>
        </h3>
        <div className="text-center text- mb-5">
          {t('projectProposal.viewDetail.code')}: {symbolString}
        </div>

        <div className="ml-4">
          <GeneralInfo
            generalInfoData={generalInfo}
            expenditureData={expenditure}
            expenditureInGeneral={expenditureInGeneral}
            isLoading={isGeneralInfoLoading}
            handleDownloadFile={handleDownloadFile}
            handleDownloadScientificProfileFile={handleDownloadScientificProfileFile}
          />
          {is1593(generalInfo?.template || generalInfo?.template) && (
            <ReasearchDescripton documents={documents} handleDownloadFile={handleDownloadFile} />
          )}
          <ResearchResult
            data={researchResult}
            researchType={generalInfo?.researchType}
            general={generalInfo}
          />
          {is1593(generalInfo?.template || generalInfo?.template) && (
            <ExpenditureSection
              expenditureData={expenditure}
              expenditureInGeneral={expenditureInGeneral}
              generalInfoData={generalInfo}
            />
          )}

          {!is1593(generalInfo?.template || generalInfo?.template) && (
            <ReasearchDescripton documents={documents} handleDownloadFile={handleDownloadFile} />
          )}
          <Documents data={documents} handleDownloadFile={handleDownloadFile} />
        </div>
      </div>
      <ErrorDialog
        title={t('projectProposal.downloadZipErrorTitle')}
        isError={isDownloadZipError}
        error={downloadZipError}
        visible={isErrorDialogOpen}
        setVisible={setIsErrorDialogOpen}
      />
      <ErrorDialog
        title={t('projectProposal.downloadZipErrorTitle')}
        isError={isDownloadExplanationError}
        error={downloadExplanationError}
        visible={isErrorDialogOpen}
        setVisible={setIsErrorDialogOpen}
      />
    </div>
  );
  // #endregion Render
}
